import React, { FC, useContext, useEffect, useMemo } from 'react';

import { useQuery } from '@apollo/client';
import {
  AlertIcon,
  Button,
  Modal,
  Preloader,
  Toggle,
} from '@smartshell/ui-kit';
import { Controller, useForm } from 'react-hook-form';

import { LocalizationContext, TLocalesKeys } from 'localization';
import { COMPANY_PERMISSION } from 'schemas';

import {
  CompanyPermissionInput,
  CompanyPermissionsResponse,
  IPermission,
  PermissionForm,
  RolesTableModalProps,
} from './interfaces';
import s from './RolesTableModal.module.scss';
import {
  getBrowsePermission,
  getHint,
  useUpdateCompanyPermissions,
} from './utils';

const RolesTableModal: FC<RolesTableModalProps> = ({ role, close }) => {
  const { data, loading } = useQuery<CompanyPermissionsResponse>(
    COMPANY_PERMISSION,
    {
      fetchPolicy: 'no-cache',
    }
  );

  const { updateCompanyPermissions, loading: updateLoading } =
    useUpdateCompanyPermissions();

  const { t } = useContext(LocalizationContext);

  const { control, handleSubmit, reset, watch } = useForm<PermissionForm>({
    mode: 'onChange',
    defaultValues: {},
  });

  const rolePermissions = useMemo<IPermission[] | null>(() => {
    if (!data) return null;

    const companyPermissions = data.companyPermissions.companyPermissions;

    const rolePermission = companyPermissions
      .find((companyRole) => companyRole.role_id === role.id)
      ?.permissions.filter(
        ({ locked, enabled }) => !locked || (locked && enabled)
      )
      .filter(({ alias }) => alias !== 'setting_browse')
      .filter(({ alias }) => alias !== 'public_key')
      .filter(({ alias }) => {
        const translationKey: TLocalesKeys = `permission.name.${alias}`;
        const translated = t(translationKey);

        return translated && translated !== translationKey;
      });
    if (!rolePermission || rolePermission?.length === 0) return null;

    return rolePermission;
  }, [data, role, t]);

  const defaultValues = useMemo<PermissionForm>(() => {
    if (!rolePermissions) return {};
    const values: PermissionForm = {};

    rolePermissions.forEach(({ alias, enabled }) => (values[alias] = enabled));

    return values;
  }, [rolePermissions]);

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues]);

  return (
    <Modal className={s.container} onClose={close}>
      <div className={s.header}>{t(`roles.${role?.alias}`)}</div>
      <Preloader loading={loading}>
        {rolePermissions && (
          <form
            onSubmit={handleSubmit((fields) => {
              const newPermissions: IPermission[] = rolePermissions
                ?.filter(({ locked }) => !locked)
                .map((permission) => {
                  if (fields[permission.alias] !== undefined) {
                    return {
                      ...permission,
                      enabled: fields[permission.alias] || false,
                    };
                  }

                  return permission;
                });

              const companyPermissions: CompanyPermissionInput[] = [
                {
                  role_id: role.id,
                  permissions: newPermissions.map(({ id, alias, enabled }) => ({
                    id,
                    alias,
                    enabled,
                  })),
                },
              ];

              updateCompanyPermissions({
                variables: {
                  input: {
                    company_permissions: companyPermissions,
                  },
                },
              }).then(() => close());
            })}
          >
            <div className={s.wrapper}>
              {rolePermissions.map((permission) => {
                const hint = getHint(permission.alias, t);
                const browsePermission = getBrowsePermission(permission.alias);
                const isBrowsePermissionEnabled = browsePermission
                  ? watch(browsePermission)
                  : null;

                return (
                  <Controller
                    key={permission.id}
                    control={control}
                    render={({ field, fieldState: { isDirty } }) => (
                      <div>
                        <Toggle
                          label={t(`permission.name.${permission.alias}`)}
                          {...field}
                          value={field.value || false}
                          disabled={permission.locked}
                        />
                        {((isDirty &&
                          field.value &&
                          hint &&
                          permission.alias === 'work_shift_open') ||
                          (field.value &&
                            hint &&
                            isBrowsePermissionEnabled === false)) && (
                          <div className={s.hint}>
                            <AlertIcon size="s" color="warning" />
                            <span>{hint}</span>
                          </div>
                        )}
                      </div>
                    )}
                    name={permission.alias}
                  />
                );
              })}
            </div>
            <Button fullWidth type="submit" disabled={updateLoading || loading}>
              {t('save')}
            </Button>
          </form>
        )}
      </Preloader>
    </Modal>
  );
};

export default RolesTableModal;
